import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import axios from "axios";
import MaterialTable from "@material-table/core";
import {TablePagination, Checkbox, Select, MenuItem, IconButton, useTheme} from "@material-ui/core";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import ContractsValidator from "../validators/ContractsValidator";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import { useContracts, useAddContract, useUpdateContract, useDeleteContract, useDuplicateContract } from "../../../services/contracts";
import {
  MaskedText,
  DatePicker, Alert, ComboSelect,
} from "../../../controls";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import fileDialog from "file-dialog";
import readFileAsync from "../../../functions/ReadFileAsync";
import DocumentViewer from "../../Documents/DocumentViewer";
import ContractSelectDialog from "./ContractSelectDialog";
import AddContractDialog from "./AddContractDialog";
import { AirplaneNoteIcon } from '../../../icons';
import GenerateFlightFixtureDialog from './GenerateFlightFixtureDialog';

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";
const today = moment().startOf("day").format("YYYY-MM-DD");

const ContractList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(ContractsValidator, true);
  const eventBus = SystemData.eventBus;

  const props = {
    ..._props
  }
  const classes = props.classes;
  const month = props.month;
  const year = props.year;

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: ""
  });
  const [viewDocuments, setViewDocuments] = useState ({
    open: false,
    documentsToView: null,
  });
  const [defaultValues, setDefaultValues] = useState(null);

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddContract = useAddContract();
  const handleUpdateContract = useUpdateContract();
  const handleDeleteContract = useDeleteContract();
  const handleDuplicateContract = useDuplicateContract();

  const queryClient = useQueryClient();
  const extraKeys = [year, month];

  const contracts = useContracts({tableRef, queryClient, extraKeys});

  const duplicateContract = async (sourceContract) => {
    if (sourceContract) {
      await handleDuplicateContract.mutate({record: sourceContract});
    }
  }

  const downloadContract = (contractFile) => {
    window.open(contractFile, "_blank");
  }

  const uploadTemplateToS3 = async (contractUuid, fileName, file) => {
    const fileContent = await readFileAsync(file);
    const buffer = new Uint8Array(fileContent);
    const dataArray = [];
    buffer.forEach((x,i)=>{dataArray[i] = x});

    const headers = {...axios.defaults.headers, 'Content-Type': 'application/json'};

    try {
      const response = await axios.post(
        `${apiSitePrefix}/contracts/upload/${contractUuid}/${fileName}`,
        { document: JSON.stringify(dataArray) },
        {
          headers,
        }
      );

      //console.error(response);
      if (response && response.data) {
        if (response.data.status) {
          setAlert({ severity: "info", open: true, message: "Upload completed successfully", timeOut: 10 });
          queryClient.invalidateQueries(['Contracts', ...extraKeys]);
        } else {
          setAlert({ severity: "error", open: true, message: "The contract file failed to upload with this error: " + response.data.error });
        }
      } else {
        setAlert({ severity: "error", open: true, message: "The contract file failed to upload due to an unknown error " });
      }
    } catch (err) {
      console.error(err);
      setAlert({ severity: "error", open: true, message: "The contract file failed to upload with this error: " + err.message });
    }
  }

  const selectFileAndUpload = async (contractUuid) => {
    const files = await fileDialog({ accept: '.docx, .pdf'});

    if (files && files.length > 0) {
      try {
        const fileName = files[0].name;
        // console.log("selected file: ", fileName);
        uploadTemplateToS3(contractUuid, fileName, files[0]);
      } catch (err) {
        console.log(err);
        setAlert({ severity: "error", open: true, message: "Failed to upload file!" });
      }
    }
  }

  const uploadContract = async (contractUuid, contractDocument) => {

    if (contractDocument && contractDocument !== "") {
      const filePathParts = contractDocument.split("/");
      const fileName = filePathParts && Array.isArray(filePathParts) && filePathParts.length > 0 ? filePathParts[filePathParts.length - 1] : "";
      SystemData.confirm(
        "Upload Contract Document",
        `Are you sure you wish to upload a new contract document file? This will overwrite the existing stored file (${fileName})`,
        [
          {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
          {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
        ],
        async (response) => {
          if (response) {
            selectFileAndUpload(contractUuid);
          }
        }
      );
    } else {
      selectFileAndUpload(contractUuid);
    }
  };

  const viewContractDocument = (contractDocument, contractName) => {
    const documentsToView = [];
    if (contractDocument && contractDocument !== "") {
      const fileNameParts = contractDocument.split(".");
      const filePathParts = contractDocument.split("/");
      const documentType = fileNameParts && Array.isArray(fileNameParts) && fileNameParts.length > 0 ? fileNameParts[fileNameParts.length - 1] : "";
      const fileName = filePathParts && Array.isArray(filePathParts) && filePathParts.length > 0 ? filePathParts[filePathParts.length - 1] : "";
      const documentDetails = {
        documentDescription: contractName,
        documentType: documentType,
        fileName: fileName,
        fileType: documentType,
        uri: contractDocument
      }
      documentsToView.push(documentDetails);
    }
    if (documentsToView.length > 0) {
      setViewDocuments({...setViewDocuments, open: true, documentsToView: documentsToView});
    }
  }

  const generateFlightFixture = (rowData) => {
    eventBus.trigger("GenerateFlightFixtureDialog.open", rowData);
  }

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/contracts/defaults`);
      if (response.data && response.data.success) {
        setDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Contract Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Contract Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Contract Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Contract Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Contract Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Contract Defaults. Server is not responding`);
    }
  }
  const executeRowAction = async (params) => {
    if (params) {
      const flightFixtureUuid = params.flightFixtureUuid;
      const tripNumber = params.tripNo;
      const tripRoute = params.tripRoute;
      const tripFlightDate = params.tripDate;
      const rowData = params.rowData;
      const mode = params.mode;
      const message = params.message;
      let url = null;

      switch (params.selectedOption) {
        case "New Flight Fixture":
          url = `${apiSitePrefix}/contracts/flightfixture/${flightFixtureUuid}`;
          break;
        default:
          break;
      }

      if (url) {
        try {
          SystemData.toggleLoadingProgress(true, message ? message : "");
          const response = await axios.get(url, {
            headers: {
              //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
              'Access-Control-Allow-Origin': '*',
            }
          });
          SystemData.toggleLoadingProgress(false, `Preparing ${params.selectedOption} ...`);

          if (response && response.data) {
            if (response.data.status) {
              if (mode && mode === "email") {
                const fileName = response.data.downloadFileName;
                const flightDate = moment(tripFlightDate, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY");
                const subject = `New Flight // ${tripNumber} // ${tripRoute} // ${flightDate}`;
                const attachmentDescription = `New flight fixture for ${tripNumber}`;
                const emailRecord = {
                  recipients: defaultValues && __.has(defaultValues, "recipients") ? defaultValues.recipients : "",
                  carbonCopy: defaultValues && __.has(defaultValues, "ccList") ? defaultValues.ccList : "",
                  subject: subject,
                  templateType: "FLIGHT_FIXTURE",
                  templateUuid: "dummyTemplateUuid",
                  attachmentDescription: attachmentDescription,
                  attachmentUrl: response.data.excelSignedUrl,
                  fileName: fileName.replaceAll("/",""),
                  attachments: "New Flight Fixture",
                  repeatData: [],
                  contactRole: "TRIP SHEET",
                  tripLegUuid: rowData.tripLegUuid,
                  additionalAttachments: response.data.additionalAttachments,
                  from: defaultValues && __.has(defaultValues, "from") ? defaultValues.from : "",
                }
                SystemData.sendEmail(emailRecord, `Send ${attachmentDescription}`, true);
              } else {
                const fileName = response.data.downloadFileName;
                let a = document.createElement('a');
                a.href = response.data.excelSignedUrl;
                a.download = fileName;
                a.style.display = "none";
                document.body.appendChild(a);
                a.click();
                setTimeout(() => { document.body.removeChild(a); }, 0);
              }
            } else {
              if (response.data.error) {
                switch (params.selectedOption) {
                  case "New Flight Fixture":
                    SystemData.openAlert(
                      "Error",
                      `${params.selectedOption} failed to action due to the following error: ${response.data.error}`
                    );
                    break;
                  default:
                    SystemData.openAlert(
                      "Download Error",
                      `The ${params.selectedOption} report could not be downloaded due to the following error: ${response.data.error}`
                    );
                }
              } else {
                SystemData.openAlert(
                  "Info",
                  `${params.selectedOption} Completed`
                )
              }
            }
          }

        } catch (err) {
          SystemData.toggleLoadingProgress(false);
          let errorText = "due to an unknown error";
          if (err.response && err.response.data && err.response.data.error) {
            errorText = `, ${err.response.data.errorMessage}`;
          }
          switch (params.selectedOption) {
            default:
              SystemData.openAlert(
                "Download Error",
                `The ${params.selectedOption} report could not be downloaded ${errorText}`
              );
          }
        }
      }
      // queryClient.invalidateQueries(['Trips', ...extraKeys]);
    }
  }

  const handleSendFlightFixture = (params) => {
    const {flightFixture, rowData} = params;
    executeRowAction({ ...__.pick(flightFixture, ["flightFixtureUuid", "tripUuid", "tripNo", "tripRoute", "tripDate"]), rowData, selectedOption: "New Flight Fixture", mode: "email" });
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:12, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        title: "Contract Name",
        field: "contractName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(205)),
        title: "Contract Type",
        field: "contractType",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        editComponent: (props) => (
          <ComboSelect
            id="contractType"
            group="CONTRACT TYPES"
            subGroup=""
            fullWidth
            value={props.value || ""}
            returnType={"object"}
            onChange={(value, returnObject) => {
              if (returnObject && !__.isEmpty(returnObject)) {
                let companyType = "ANY";
                let contractedPartyUuid = props.rowData.contractedPartyUuid;
                if (__.has(returnObject, "data") && __.has(returnObject.data, "companyType")) {
                  companyType = returnObject.data.companyType;
                }
                if (props.rowData.companyType !== companyType)
                  contractedPartyUuid = null;
                props.onRowDataChange({...props.rowData, contractType: value, companyType: companyType, contractedPartyUuid});
              } else {
                props.onChange(value);
              }
            }}
          />
        ),
        render: (rowData) => (
          <ComboSelect
            group="CONTRACT TYPES"
            subGroup=""
            fullWidth
            value={rowData.contractType}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(300)),
        title: "Contracted Party",
        field: "contractedPartyUuid",
        emptyValue: null,
        initialEditValue: null,
        editable: ((cell, rowData, updateData) => {console.log("r:", rowData, "u:", updateData); return (updateData.companyType && updateData.companyType !=="") }),
        editComponent: (props) => (
          <CompanyUuidLookup
            companyType={props.rowData.companyType ? props.rowData.companyType  : props.rowData.contractType === "CHARTER" ? "CLIENT" : (props.rowData.contractType === "SGHA" ? "SUPPLIER" : "ANY")}
            // companyType={"CLIENT"}
            value={props.rowData.contractedPartyUuid}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const contractedParty = returnObject.data
                const data = {
                  ...props.rowData,
                  "company.companyName": contractedParty.companyName,
                  contractedPartyUuid: contractedParty.companyUuid,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
        render: (rowData) => (
          <CompanyUuidLookup
            value={rowData.contractedPartyUuid}
            type="render"
            hideCode={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(200)),
        title: "Reference",
        field: "contractReference",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Revision",
        field: "contractRevision",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(10)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70, undefined, undefined, {textAlign: 'center'}, {textAlign: 'center'})),
        title: "Active",
        field: "active",
        type: "boolean",
        sorting: false,
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.active === true} value="true" />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Start Date",
        field: "contractStartDate",
        type: "date",
        //emptyValue: today,
        //initialEditValue:today,
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "End Date",
        field: "contractEndDate",
        type: "date",
        //emptyValue: today,
        //initialEditValue:today,
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
    ];
  };

  const padding = "dense";

  if (!defaultValues || __.isEmpty(defaultValues))
    getDefaultValues();

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <Alert
        onChange={(open) => {
          setAlert({ ...alert, open })
        }}
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        timeOut={alert.timeOut || 0}
      />
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: padding,
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          // (rowData) => (
          //   {
          //     hidden: false,
          //     disabled: !state.canEdit,
          //     icon: 'airplane_ticket',
          //     iconProps: { style: (state.canEdit) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
          //     tooltip: "New Flight Fixture",
          //     isFreeAction: false,
          //     onClick: (e, rowData) => {
          //       viewContractDocument(rowData.contractDocument, rowData.contractName);
          //     }
          //   }
          // ),
          (rowData) => (
            {
              hidden: false,
              disabled: !state.canEdit,
              icon: 'description_outlined',
              iconProps: { style: (state.canEdit && rowData.contractDocument) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
              tooltip: "View Contract File",
              isFreeAction: false,
              onClick: (e, rowData) => {
                viewContractDocument(rowData.contractDocument, rowData.contractName);
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: !state.canEdit || !rowData.contractDocument,
              icon: 'get_app',
              iconProps: { style: (state.canEdit && rowData.contractDocument) ? { color: theme.palette.primary.main, marginTop: 4 } : {color: '#b3b0b0', marginTop: 4}},
              tooltip: "Download Contract File" + (!rowData.contractDocument ? " - Contract file wasn't uploaded yet" : ""),
              isFreeAction: false,
              onClick: (e, rowData) => {
                downloadContract(rowData.contractDocument);
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: !state.canEdit,
              icon: 'publish',
              iconProps: { style: (state.canEdit) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
              tooltip: "Upload Contract File",
              isFreeAction: false,
              onClick: (e, rowData) => {
                uploadContract(rowData.contractUuid, rowData.contractDocument);
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: !state.canEdit,
              icon: () =>(<AirplaneNoteIcon style={{position:"relative", top: "2px", stroke: theme?.palette?.primary?.main, fill: theme?.palette?.primary?.main}}/>),
              iconProps: { style: (state.canEdit) ? { color: "secondary" } : {color: '#b3b0b0'}},
              tooltip: "Generate Flight Fixture",
              isFreeAction: false,
              onClick: (e, rowData) => {
                generateFlightFixture(rowData);
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: !rowData.active,
              icon: 'file_copy',
              iconProps: { style: (rowData.active) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
              tooltip: "Duplicate Contract",
              isFreeAction: false,
              onClick: (e, rowData) => {
                duplicateContract(rowData);
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              // disabled: state.canEdit,
              icon: 'edit',
              // iconProps: { style: (state.canEdit) ? { color: "secondary" } : {color: '#b3b0b0'}},
              iconProps: { style: { color: "secondary" }},
              tooltip: "Edit Contract",
              isFreeAction: false,
              onClick: (e, rowData) => {
                SystemData.eventBus.trigger("AddContractDialog.edit", rowData);
              }
            }
          ),
          {
            hidden: false,
            disabled: !state.canEdit,
            icon: 'add_box',
            tooltip: "Add Contract",
            isFreeAction: true,
            onClick: (e, rowData) => {
              if (SystemData.eventBus) {
                SystemData.eventBus.trigger("AddContractDialog.open", {title: "Add Contract", canEdit: state.canEdit});
              }
            }
          }
        ]}

        title="Contract Management"
        columns={getColumns({canEdit: state.canEdit})}
        detailPanel={
          []
        }
        data={contracts.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: undefined,
          onRowUpdate: undefined,
          // onRowUpdate: (newData, oldData) =>
          // new Promise((resolve, reject) => {
          //   handleUpdateContract.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
          // }),
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteContract.mutate({oldRecord: oldData, resolve, reject, extraKeys});
            }),
        }}
      />
      {viewDocuments.open
        ?
          <DocumentViewer
            dialogStates={
              {showDialog: viewDocuments.open,
                title: `Contract Viewer`,
                record: viewDocuments.documentsToView[0]
              }
            }
            close={()=>{setViewDocuments({...viewDocuments, open: false})}}
            documents={viewDocuments.documentsToView}
          />
        :
          <></>
      }
      <ContractSelectDialog
        systemData={SystemData}
        onChange={(result) => {console.log(result)}}
      />
      <AddContractDialog
        SystemData={SystemData}
        apiSitePrefix={apiSitePrefix}
        extraKeys={[]}
        handleAdd={handleAddContract}
        handleUpdate={handleUpdateContract}
        classes={classes}
        defaultValues={{}}
        canEdit={state.canEdit}
      />
      <GenerateFlightFixtureDialog
        SystemData={SystemData}
        apiSitePrefix={apiSitePrefix}
        classes={classes}
        postSave={handleSendFlightFixture}
      />
    </div>
  );

}

export default ContractList;
